import * as React from "react";
import classNames from "classnames";
import { storeIcon } from "@lululemon/ecom-pattern-library-icons";
import { AccordionItemV2 } from "@lululemon/ecom-pattern-library";
import BopisAccordionNearbyStores from "./components/bopis-accordion-nearby-stores";
// import { Accordion } from "@lululemon/ecom-pattern-library-accordion";
// import { AccordionItem } from "@lululemon/ecom-pattern-library-accordion-item";
import {
  cantFindItemInNearbyStoresMessage,
  selectASizeMessage
} from "./constants";
import styles from "./bopis-accordion.module.scss";
import BopisAccordionNoLocationMessage from "./components/bopis-accordion-no-location-message";

/**
 * @param {object|undefined} activeSku - An object containing information about the currently selected SKU.
 * - `id` {string} - The unique identifier of the SKU.
 * - `skuUrl` {string} - The URL of the SKU's product page.
 * - `price` {object} - The price details of the SKU.
 *   - `listPrice` {string} - The listed price of the product.
 *   - `currency` {object} - Currency details.
 *     - `code` {string} - The currency code (e.g., "USD").
 *     - `symbol` {string} - The currency symbol (e.g., "$").
 *   - `onSale` {boolean} - Whether the product is on sale.
 *   - `salePrice` {string|null} - The sale price if applicable, otherwise `null`.
 * - `size` {string} - The selected size of the SKU.
 * - `color` {object} - The color details of the SKU.
 *   - `name` {string} - The name of the color.
 *   - `code` {string} - The color code.
 * - `available` {boolean} - Indicates whether the SKU is available for purchase.
 * - `styleId` {string} - The style identifier of the SKU.
 * @param {string} deliveryType - The type of delivery option selected "PIS" - "SHP".
 * @param {boolean} isAccordionOpen - A boolean that determines if the accordion is open or closed.
 * @param {Array<Object>|null|undefined} nearbyBopisStores - An array containing all nearby stores with inventory.
 * - `id` {string} - The unique identifier of the store.
 * - `name` {string} - The name of the store.
 * - `distanceToStore` {number} - Distance of the store from the user's location in miles.
 * - `address` {object} - The address details of the store.
 *   - `address1` {string} - The first line of the store address.
 *   - `address2` {string|null} - The second line of the store address (if available).
 *   - `city` {string} - The city where the store is located.
 *   - `state` {string} - The state where the store is located.
 *   - `country` {string} - The country where the store is located.
 * @param {string} selectedStoreId - The ID of the currently selected store.
 * @param {Array<Object>} selectedSkuQuantities - An array containing all selected SKU quantities.
 * - `skuId` {string} - The unique identifier of the SKU.
 * - `quantity` {string} - The selected quantity of the SKU.
 * - `bopisQuantity` {string} - The available quantity for BOPIS.
 * @param {object} userLocation - An object containing the geolocation details of the user.
 * - `lat` {number} - The latitude of the user's location.
 * - `lng` {number} - The longitude of the user's location.
 * - `address` {string|null} - The address of the user (if available).
 * - `city` {string} - The city where the user is located.
 * - `state` {string} - The state where the user is located.
 * @param {string} userCountry - The country of the user in ISO format "US" - "CA".
 * @param {function} handleChangeLocations - A function to handle the change of the selected store location.
 * @param {function} handleClick - A function that runs on the click of the accordion.
 * @param {function} onChangeHandler - A function to handle the on-change event from the radio button.
 */
const BopisAccordion = ({
  activeSku,
  deliveryType,
  handleChangeLocations,
  handleClick,
  isAccordionOpen,
  nearbyBopisStores,
  selectedStoreId,
  selectedSkuQuantities,
  userCountry,
  userLocation,
  onChangeHandler
}) => {
  const hasUserLocation =
    userLocation?.city != null || userLocation?.address != null;

  const displayBopisAccordionContent = () => {
    if (activeSku?.size == null) {
      return <p className={styles.messageText}>{selectASizeMessage}</p>;
    }
    if (!hasUserLocation) {
      return (
        <BopisAccordionNoLocationMessage
          handleChangeLocations={handleChangeLocations}
          userCountry={userCountry}
        />
      );
    }
    if (nearbyBopisStores?.length === 0) {
      return (
        <p className={styles.messageText}>
          {cantFindItemInNearbyStoresMessage}
        </p>
      );
    }
    return (
      <BopisAccordionNearbyStores
        activeSku={activeSku}
        handleChangeLocations={handleChangeLocations}
        nearbyBopisStores={nearbyBopisStores}
        selectedStoreId={selectedStoreId}
        selectedSkuQuantities={selectedSkuQuantities}
        userLocation={userLocation}
        userCountry={userCountry}
        onChangeHandler={onChangeHandler}
      />
    );
  };

  // BOPISTODO - the commented code is for the new pattern library accordion but it is broken currently.
  // Therefore, we are using the old version until a fix is deployed for v6.
  return (
    // <Accordion
    //   className={classNames(styles.accordionHeadingItemWrapper, "OneLinkTx", {
    //     [styles.noStoreSelected]: selectedStore.id == null,
    //     [styles.storeSelected]: selectedStore.id,
    //   })}
    //   onClickAccordionItem={handleClick}
    //   v2
    // >
    //   <AccordionItem
    //     expanded={isAccordionOpen}
    //     heading="Pick up in store"
    //     icon={storeIcon}
    //     analyticsId="pdp-pick-up-in-store"
    //     className={styles.accordionItem}
    //   >
    //     {displayBopisAccordionContent()}
    //   </AccordionItem>
    // </Accordion>
    <AccordionItemV2
      expanded={isAccordionOpen}
      heading="Pick up in store"
      handleClick={handleClick}
      icon={storeIcon}
      analyticsId="pdp-pick-up-in-store"
      className={classNames(styles.accordionStyle, "OneLinkTx", {
        [styles.accordionHeadingRegular]: deliveryType === "SHP",
        [styles.accordionHeadingBold]: deliveryType === "PIS"
      })}
    >
      {displayBopisAccordionContent()}
    </AccordionItemV2>
  );
};

export default BopisAccordion;
